import { isDefined } from 'tools/common'
import { InvoiceSummaryInfo } from 'types/invoice'
import { OverAdvanceInfo } from 'types/overAdvance'
import { AppliedInfo, AppliedOAInfo, PaymentLocalStorage, PaymentUserStorage } from 'types/payments'

const useLocalStoragePayment = (paymentId?: string, userId?: string) => {
  let paymentUserStorages: PaymentUserStorage[] = getPaymentUserStorages()
  let paymentUserStorage: PaymentUserStorage | undefined = getPaymentUserStorage()
  let paymentStatuses: PaymentLocalStorage[] = getPaymentStatuses()
  let paymentStatus: PaymentLocalStorage | undefined = getPaymentStatus()
  let appliedInfos: AppliedInfo[] = getAppliedInfos()
  let appliedOAInfos: AppliedOAInfo[] = getAppliedOAInfos()

  function getPaymentUserStorages() {
    return JSON.parse(localStorage.getItem('paymentUserStorage') || '[]')
  }

  function getPaymentUserStorage() {
    return paymentUserStorages.find((item) => item.userId === userId)
  }

  function getPaymentStatuses() {
    return paymentUserStorage?.paymentStatuses || []
  }

  function getPaymentStatus(id: string | undefined = paymentId) {
    return paymentStatuses.find((item) => item.paymentId === id)
  }

  function getAppliedInfos() {
    return paymentStatus?.appliedInfos || []
  }

  function getAppliedInfo(invoiceId: string) {
    updateLastLocalStorageState()
    return appliedInfos.find((item) => item.invoiceId === invoiceId)
  }

  function getAppliedOAInfos() {
    return paymentStatus?.appliedOAInfos || []
  }

  function getAppliedOAInfo(overAdvanceId: string) {
    updateLastLocalStorageState()
    return appliedOAInfos.find((item) => item.overAdvanceId === overAdvanceId)
  }

  function updateLastLocalStorageState() {
    paymentUserStorages = getPaymentUserStorages()
    paymentUserStorage = getPaymentUserStorage()
    paymentStatuses = getPaymentStatuses()
    paymentStatus = getPaymentStatus()
    appliedInfos = getAppliedInfos()
    appliedOAInfos = getAppliedOAInfos()
  }

  const updateSelectedInvoices = (selectedInvoices: InvoiceSummaryInfo[]) => {
    updateLastLocalStorageState()
    if (isDefined(paymentStatus)) {
      const invoiceIds = selectedInvoices.map((invoice) => invoice.id)
      paymentStatus.selectedInvoices = selectedInvoices
      paymentStatus.appliedInfos = appliedInfos.filter((item) => invoiceIds.includes(item.invoiceId))
      savePaymentStatus(paymentUserStorages)
    }
  }

  const updateAppliedInfo = (invoiceId: string, applyType: string, appliedAmount: number | null) => {
    updateLastLocalStorageState()
    if (isDefined(paymentStatus)) {
      const appliedInfo = getAppliedInfo(invoiceId)
      if (isDefined(appliedInfo)) {
        appliedInfo.appliedAmount = appliedAmount
        appliedInfo.applyType = applyType as 'FULL' | 'PART' | 'OVER'
      } else {
        appliedInfos.push({ invoiceId, appliedAmount, applyType: applyType as 'FULL' | 'PART' | 'OVER' })
      }
      paymentStatus.appliedInfos = appliedInfos
      savePaymentStatus(paymentUserStorages)
    }
  }

  const updateApplyType = (invoiceId: string, applyType: string) => {
    updateLastLocalStorageState()
    if (isDefined(paymentStatus)) {
      const appliedInfo = getAppliedInfo(invoiceId)
      if (isDefined(appliedInfo)) {
        appliedInfo.applyType = applyType === 'full' ? 'FULL' : 'PART'
      }
      savePaymentStatus(paymentUserStorages)
    }
  }

  const removePaymentStatus = (paymentId: string) => {
    updateLastLocalStorageState()
    paymentStatuses = paymentStatuses.filter((item) => item.paymentId !== paymentId)
    if (isDefined(paymentUserStorage)) {
      paymentUserStorage.paymentStatuses = paymentStatuses
    }
    savePaymentStatus(paymentUserStorages)
  }

  const storeNewStatus = () => {
    updateLastLocalStorageState()
    if (isDefined(paymentUserStorage)) {
      addNewPaymentStatuses(paymentUserStorage)
    } else {
      addNewUserStorage()
    }
    savePaymentStatus(paymentUserStorages)
  }

  const addNewPaymentStatuses = (paymentUserStorage: PaymentUserStorage) => {
    if (isDefined(paymentId)) {
      if (!isDefined(paymentUserStorage.paymentStatuses)) {
        paymentUserStorage.paymentStatuses = [{ paymentId: paymentId, storeDate: new Date().toISOString() }]
      } else {
        if (!isDefined(paymentStatus)) {
          if (paymentUserStorage.paymentStatuses.length >= 100) {
            removeOldPaymentStatuses(paymentUserStorage.paymentStatuses)
          }
          paymentUserStorage.paymentStatuses.push({ paymentId: paymentId, storeDate: new Date().toISOString() })
        }
      }
    }
  }

  const removeOldPaymentStatuses = (paymentStatuses: PaymentLocalStorage[]) => {
    paymentStatuses.sort((a, b) => new Date(a.storeDate).getTime() - new Date(b.storeDate).getTime())
    paymentStatuses.splice(0, 10)
  }

  const addNewUserStorage = () => {
    if (isDefined(userId) && isDefined(paymentId)) {
      const newPaymentUserStorage: PaymentUserStorage = {
        userId: userId,
        paymentStatuses: [{ paymentId: paymentId, storeDate: new Date().toISOString() }],
      }
      paymentUserStorages.push(newPaymentUserStorage)
    }
  }

  const savePaymentStatus = (newPaymentUserStorage: PaymentUserStorage[]) => {
    localStorage.setItem('paymentUserStorage', JSON.stringify(newPaymentUserStorage))
  }

  const getStoredSelectedInvoices = () => {
    updateLastLocalStorageState()
    return paymentStatus?.selectedInvoices || []
  }

  const hasStoredInvoiceSelection = () => {
    updateLastLocalStorageState()
    return isDefined(paymentStatus?.selectedInvoices)
  }

  const updateSelectedOverAdvances = (selectedOverAdvances: OverAdvanceInfo[]) => {
    updateLastLocalStorageState()
    if (isDefined(paymentStatus)) {
      const oaIds = selectedOverAdvances.map((oa) => oa.id)
      paymentStatus.selectedOverAdvances = selectedOverAdvances
      paymentStatus.appliedOAInfos = appliedOAInfos.filter((item) => oaIds.includes(item.overAdvanceId))
      savePaymentStatus(paymentUserStorages)
    }
  }

  const updateAppliedOAInfo = (overAdvanceId: string, appliedAmount: number | null) => {
    updateLastLocalStorageState()
    if (isDefined(paymentStatus)) {
      const existing = getAppliedOAInfo(overAdvanceId)
      if (isDefined(existing)) {
        existing.appliedAmount = appliedAmount
      } else {
        appliedOAInfos.push({ overAdvanceId, appliedAmount })
      }
      paymentStatus.appliedOAInfos = appliedOAInfos
      savePaymentStatus(paymentUserStorages)
    }
  }

  const getStoredSelectedOverAdvances = () => {
    updateLastLocalStorageState()
    return paymentStatus?.selectedOverAdvances || []
  }

  return {
    getPaymentUserStorages,
    getPaymentUserStorage,
    getPaymentStatuses,
    getPaymentStatus,
    updateSelectedInvoices,
    updateAppliedInfo,
    updateApplyType,
    removePaymentStatus,
    storeNewStatus,
    savePaymentStatus,
    getStoredSelectedInvoices,
    hasStoredInvoiceSelection,
    getAppliedInfo,
    getAppliedInfos,
    getStoredSelectedOverAdvances,
    updateSelectedOverAdvances,
    updateAppliedOAInfo,
    getAppliedOAInfo,
    getAppliedOAInfos,
  }
}

export default useLocalStoragePayment
